import type { CSSProperties, FC, ReactNode } from 'react'
import type { TileCell } from '@/components/landing/tiles'

import { useId, useState } from 'react'
import { Reveal } from '@/components/generals/reveal'
import { CountUp } from '@/components/generals/count-up'
import { TrialButton } from '@/components/generals/trial-button'
import { SectionBadge } from '@/components/landing/section-badge'
import { TileField } from '@/components/landing/tiles'

// Teams on Fieldhand hand an average of 40% of their repetitive hours to agents.
const AUTOMATION_SHARE = 0.4
const WEEKS_PER_MONTH = 52 / 12

const LEFT_TILES: TileCell[] = [[0, 0], [2, 0], [1, 1], [0, 2]]

const currency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
})

const whole = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
})

interface SliderProps {
    label: string
    hint: string
    min: number
    max: number
    step: number
    value: number
    onChange: (value: number) => void
    format: (value: number) => string
}

const Slider: FC<SliderProps> = ({ label, hint, min, max, step, value, onChange, format }): ReactNode => {
    const id = useId()
    const fill = ((value - min) / (max - min)) * 100

    return (
        <div>
            <div className='flex items-baseline justify-between gap-4'>
                <label htmlFor={id} className='text-[15.5px] font-medium text-ink'>
                    {label}
                </label>

                <output htmlFor={id} className='font-heading text-[24px] leading-none font-semibold tracking-[-0.02em] text-ink tabular-nums'>
                    {format(value)}
                </output>
            </div>

            <p className='mt-1 text-[13.5px] text-muted-foreground'>
                {hint}
            </p>

            <input
                id={id}
                type='range'
                min={min}
                max={max}
                step={step}
                value={value}
                aria-valuetext={format(value)}
                onChange={(event) => onChange(Number(event.target.value))}
                className='range-moss mt-5'
                style={{ '--fill': `${fill}%` } as CSSProperties}
            />

            <div aria-hidden className='mt-2 flex justify-between text-[12px] text-muted-foreground'>
                <span>{format(min)}</span>
                <span>{format(max)}</span>
            </div>
        </div>
    )
}

interface ResultTicketProps {
    label: string
    value: string
    note: string
}

const ResultTicket: FC<ResultTicketProps> = ({ label, value, note }): ReactNode => {
    return (
        <div className='relative min-h-[168px]'>
            <div aria-hidden className='ticket absolute inset-0 rounded-[30px] bg-meadow [--notch:18px] sm:rounded-[34px] sm:[--notch:22px]' />

            <div className='relative flex h-full flex-col justify-between gap-5 px-7 py-6 sm:px-9 sm:py-7'>
                <p className='text-[15px] font-medium text-ink'>
                    {label}
                </p>

                <div>
                    <p className='font-heading text-[clamp(2.4rem,4vw,3.3rem)] leading-none font-semibold tracking-[-0.035em] text-ink tabular-nums'>
                        <CountUp value={value} duration={700} />
                    </p>

                    <p className='mt-2 text-[13px] text-ink/70'>
                        {note}
                    </p>
                </div>
            </div>
        </div>
    )
}

export const ValueSection: FC = (): ReactNode => {
    const [teamSize, setTeamSize] = useState(40)
    const [weeklyHours, setWeeklyHours] = useState(6)
    const [hourlyCost, setHourlyCost] = useState(55)

    const monthlyHours = teamSize * weeklyHours * WEEKS_PER_MONTH * AUTOMATION_SHARE
    const monthlyValue = monthlyHours * hourlyCost

    return (
        <section
            id='value'
            aria-labelledby='value-title'
            className='relative isolate overflow-hidden bg-white py-24 sm:py-32'
        >
            <TileField cells={LEFT_TILES} size='64px' className='top-16 -left-5 max-lg:hidden' />

            <Reveal className='mx-auto flex max-w-3xl flex-col items-center px-5 text-center'>
                <SectionBadge>Estimate your impact</SectionBadge>

                <h2
                    id='value-title'
                    className='mt-6 font-heading text-[clamp(2.1rem,3.1vw,2.8rem)] leading-[1.18] tracking-[-0.03em] text-balance text-ink'
                >
                    Put a number on <br className='max-sm:hidden' />
                    the time you get back.
                </h2>

                <p className='mt-5 max-w-[54ch] text-[17px] leading-relaxed text-muted-foreground'>
                    Move the sliders to see what agents could return to your team every month.
                </p>
            </Reveal>

            <Reveal delay={120} className='mx-auto mt-14 grid w-full max-w-[1100px] gap-5 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr]'>
                <div className='flex flex-col justify-center gap-10 rounded-[34px] border border-border bg-white p-7 shadow-[0_30px_80px_-60px_rgba(20,23,15,0.45)] sm:p-10'>
                    <Slider
                        label='People on the team'
                        hint='Everyone who does repetitive, rules-based work.'
                        min={5}
                        max={500}
                        step={5}
                        value={teamSize}
                        onChange={setTeamSize}
                        format={(value) => whole.format(value)}
                    />

                    <Slider
                        label='Repetitive hours per person'
                        hint='Per week: data entry, triage, follow-ups, reports.'
                        min={1}
                        max={20}
                        step={1}
                        value={weeklyHours}
                        onChange={setWeeklyHours}
                        format={(value) => `${value} h`}
                    />

                    <Slider
                        label='Average hourly cost'
                        hint='Salary plus overhead, per person.'
                        min={20}
                        max={150}
                        step={5}
                        value={hourlyCost}
                        onChange={setHourlyCost}
                        format={(value) => currency.format(value)}
                    />
                </div>

                <div className='flex flex-col gap-5'>
                    <ResultTicket
                        label='Hours back every month'
                        value={whole.format(monthlyHours)}
                        note='Based on the 40% average that Fieldhand teams hand to agents.'
                    />

                    <ResultTicket
                        label='Value created every month'
                        value={currency.format(monthlyValue)}
                        note='Time your people can spend on the work only they can do.'
                    />

                    <div className='flex flex-col items-start gap-5 rounded-[30px] bg-moss px-7 py-7 text-white sm:flex-row sm:items-center sm:justify-between sm:rounded-[34px] sm:px-9'>
                        <div>
                            <p className='text-[14px] text-white/75'>
                                Every year, that adds up to
                            </p>

                            <p className='mt-1 font-heading text-[clamp(2rem,3.2vw,2.6rem)] leading-none font-semibold tracking-[-0.03em] tabular-nums'>
                                <CountUp value={currency.format(monthlyValue * 12)} duration={700} />
                            </p>
                        </div>

                        <TrialButton
                            source='calculator'
                            className='bg-white text-ink hover:bg-sage focus-visible:ring-white focus-visible:ring-offset-moss'
                        >
                            Start saving hours
                        </TrialButton>
                    </div>
                </div>
            </Reveal>
        </section>
    )
}
